import React, {
	useState,
	useCallback,
	useEffect,
	useMemo
} from "react";
import "./StatusBar.scss";
import { styled } from "@mui/material/styles";
import {
	Toolbar,
	Button,
	Box,
	MenuItem,
	FormControl,
	InputLabel,
	Select,
	Typography
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import UseFetchDataDD from "./SellerDropDown";
import AlertBar from "../AlertBar/AlertBar";
import {
	useDispatch,
	useSelector
} from "react-redux";
import { selectedSellerRDX } from "../../../store/rootSlice";
import { DataContext } from "../../../App";
import { ThemeToggle } from "../StatusBar/ThemeToggle";
import { Logo } from "./logo";
import { useNavigate } from "react-router-dom";

export default function StatusBar({
	onHandleDrawer, isDrawerVisible, drawerWidth, onSignOut,
	marginTop, userdata, bannerdata, check, change, isLeftMenuVisible
}) {

	const handleDrawerOpen = () => {
		onHandleDrawer(true);
	};

	const AppBar = styled(MuiAppBar, {
		shouldForwardProp: (prop) => prop !== "open",
	})(({ theme, open }) => ({
		zIndex: theme.zIndex.drawer + 1,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		...(open && {
			marginLeft: drawerWidth,
			width: `calc(100% - ${drawerWidth}px)`,
			transition: theme.transitions.create(["width", "margin"], {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		}),
	}));

	const dispatch = useDispatch();
	const context = React.useContext(DataContext);
	const sellers = UseFetchDataDD(useCallback(() => context.dataProvider.getSellers(), [context.dataProvider]));
	const navigate = useNavigate();
	const reduxSeller = useSelector((state) => state.SelectedSellerRDX);

	const storedSellerRaw = localStorage.getItem("SelectedSeller");
	const storedSeller = useMemo(() => {
		if (!storedSellerRaw) {
			return null;
		}
		try {
			return JSON.parse(storedSellerRaw);
		} catch {
			return null;
		}
	}, [storedSellerRaw]);

	const displayName = userdata?.user_name || "";

	const [state, setState] = useState({
		seller: storedSeller || reduxSeller || {},
	});

	// Prefer local state, then store/localStorage, for the seller dropdown value.
	const displaySeller = state.seller?.id
		? state.seller
		: (reduxSeller?.id ? reduxSeller : (storedSeller?.id ? storedSeller : state.seller));

	// Key off GET /sellers membership, not the system role name "seller".
	// Wait until the sellers fetch has finished before treating an empty list as "no access".
	useEffect(() => {
		if (!sellers.hasFetched || sellers.isLoading) {
			return;
		}

		if (storedSeller?.id) {
			setState((previousState) => {
				if (previousState.seller?.id === storedSeller.id) {
					return previousState;
				}
				return {
					...previousState,
					seller: storedSeller,
				};
			});
			if (!reduxSeller?.id || reduxSeller.id !== storedSeller.id) {
				dispatch(selectedSellerRDX(storedSeller));
			}
			return;
		}

		if (sellers.results.length > 0) {
			const seller = sellers.results[0];
			dispatch(selectedSellerRDX(seller));
			localStorage.setItem("SelectedSeller", JSON.stringify(seller));
			setState((previousState) => ({
				...previousState,
				seller,
			}));
			return;
		}

		dispatch(selectedSellerRDX({}));
		localStorage.removeItem("SelectedSeller");
		setState((previousState) => ({
			...previousState,
			seller: {},
		}));
		navigate("/");
	}, [
		storedSeller,
		sellers.hasFetched,
		sellers.isLoading,
		sellers.results,
		reduxSeller?.id,
		dispatch,
		navigate,
	]);

	const handleChange = (e) => {
		const value = e.target.value;
		const nextSeller = sellers.results.find(seller => seller.id === value);
		setState(previousState => {
			return {
				...previousState,
				seller: nextSeller,
			};
		});
		dispatch(selectedSellerRDX(nextSeller));
		localStorage.setItem("SelectedSeller", JSON.stringify(nextSeller));
	};

	const handleSignOut = () => {
		onSignOut();
		localStorage.removeItem("SelectedSeller");
	};

	return (
		<Box className="AppBar">

			<AppBar
				position="fixed"
				open={isDrawerVisible}
				sx={{ marginTop: marginTop }}
				color="appbar"
				enableColorOnDark
			>
				<AlertBar bannerdata={bannerdata} />

				<Toolbar>

					{isLeftMenuVisible &&
						<IconButton
							color="primary"
							aria-label="open drawer"
							onClick={handleDrawerOpen}
							edge="start"
							sx={{
								marginRight: 2,
								...(isDrawerVisible && { display: "none" }),
							}}
							size="large">
							<MenuIcon />
						</IconButton>
					}

					<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
						<Logo />
						{displayName && (
							<Typography
								variant="body2"
								noWrap
								sx={{ color: "#FFFFFF", opacity: 0.95, flexShrink: 0 }}
								title={displayName}
							>
								Welcome, {displayName}
							</Typography>
						)}
					</Box>

					<Box sx={{ flexGrow: 1, minWidth: 8 }} />

					<ThemeToggle
						check={check} 
						change={change} 
					/>

					{(sellers.results.length > 1) ?
						(<FormControl sx={{ minWidth: 120, maxWidth: 120 }} size="small">
							<InputLabel id="seller-sellect-dd">Seller</InputLabel>
							<Select
								labelid="seller-sellect-dd"
								id="seller-sellect"
								value={displaySeller?.id || ""}
								onChange={handleChange}
							>
								{sellers.results.map((seller) =>
									<MenuItem
										key={seller.id}
										value={seller.id}
									>
										{seller.code}:{seller.id} - {seller.name}
									</MenuItem>
								)}
							</Select>
						</FormControl>)
						: (sellers.hasFetched && !sellers.isLoading && sellers.results.length === 0) ?
							(
								<Typography
									variant="body2"
									sx={{ mr: 2, whiteSpace: "nowrap", opacity: 0.85 }}
									title="No sellers available for this account"
								>
									No sellers
								</Typography>
							)
							: null}

					<Box ml={3}>
						<Button
							color="primary"
							variant="outlined"
							onClick={handleSignOut}
							sx={{ whiteSpace: "nowrap", backgroundColor: "transparent"}}
							title="Sign Out"
						>
							<LogoutIcon className="display-min-width" fontSize="medium" />
							<Typography className="display-max-width">Sign Out</Typography>
						</Button>
					</Box>

				</Toolbar>

			</AppBar>
		</Box>
	);

}
